import React, { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Table, Card, Button, Space } from "antd"
import { CreateEditCountryGroupModal } from "./create-edit-country-group-modal"
import { ManageCountriesModal } from "./manage-countries-modal"
import { CountryGroupDto } from "../store/types"
import { CountryTagList } from "./shared/CountryTagList"
import { createDateColumn, handleDeleteWithConfirm } from "../utils"

export const CountryGroupsTable: FC = observer(() => {
  const { dataStore: { countryGroupsStore } } = useStore()
  const [showModal, setShowModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState<CountryGroupDto | null>(null)
  const [showManageModal, setShowManageModal] = useState(false)
  const [managingGroup, setManagingGroup] = useState<CountryGroupDto | null>(null)

  useEffect(() => {
    countryGroupsStore.getAllCountryGroups()
  }, [countryGroupsStore])

  const handleDelete = (group: CountryGroupDto) => {
    handleDeleteWithConfirm(
      `country group "${group.name}"`,
      () => countryGroupsStore.deleteCountryGroup(group.id),
      "Country group deleted",
      "Failed to delete country group",
    )
  }

  const columns = [
    {
      title: "ID",
      key: "id",
      dataIndex: "id",
      width: 200,
    },
    {
      title: "Name",
      key: "name",
      dataIndex: "name",
      width: 200,
    },
    {
      title: "Countries",
      key: "countries",
      dataIndex: "countries",
      width: 400,
      render: (countries: string[]) => <CountryTagList countries={countries} />,
    },
    createDateColumn("Created At", "createdAt"),
    createDateColumn("Updated At", "updatedAt"),
    {
      title: "Actions",
      key: "actions",
      width: 250,
      fixed: "right" as const,
      render: (_: any, record: CountryGroupDto) => (
        <Space>
          <Button
            type="link"
            onClick={e => {
              e.stopPropagation()
              setManagingGroup(record)
              setShowManageModal(true)
            }}
          >
            Manage Countries
          </Button>
          <Button
            type="link"
            danger
            onClick={e => {
              e.stopPropagation()
              handleDelete(record)
            }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Button
        type="primary"
        onClick={() => {
          setEditingGroup(null)
          setShowModal(true)
        }}
        style={{ marginBottom: 16 }}
      >
        Create Country Group
      </Button>
      <Card title="Country Groups" size="small">
        <Table
          dataSource={countryGroupsStore.countryGroups}
          columns={columns}
          rowKey="id"
          loading={countryGroupsStore.isLoading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: "max-content" }}
          onRow={record => ({
            style: { cursor: "pointer" },
            onClick: () => {
              setEditingGroup(record)
              setShowModal(true)
            },
          })}
        />
      </Card>
      <CreateEditCountryGroupModal
        visible={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingGroup(null)
        }}
        initialData={editingGroup}
      />
      {managingGroup && (
        <ManageCountriesModal
          visible={showManageModal}
          onClose={async() => {
            setShowManageModal(false)
            setManagingGroup(null)
            await countryGroupsStore.getAllCountryGroups()
          }}
          countryGroup={managingGroup}
        />
      )}
    </div>
  )
})
